import { Icon } from '@brandbank/portal-components';
import NiceModal from '@ebay/nice-modal-react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  List,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import React from 'react';
import { CONNECT_MAX_T1_ACCESS_DESCRIPTION } from '../connect-max.constants';
import { IdShotItem } from '../connect-max.types';
import IdShotSelectModal from './IdShotSelectModal';

type IdShotWidgetProps = {
  idShot: IdShotItem;
  setIdShot: React.Dispatch<React.SetStateAction<IdShotItem>>;
};

const IdShotWidget = ({ idShot, setIdShot }: IdShotWidgetProps) => {
  const showEditIdShotModal = async () => {
    const newValue: IdShotItem = await NiceModal.show(IdShotSelectModal, {
      initialSelected: idShot,
    });

    newValue && setIdShot(newValue);
  };

  const BoldIfIncludesStatus: React.FC<{ children: React.ReactNode }> = ({
    children,
  }) => {
    if (typeof children === 'string') {
      const regex = /(Locked|Unlocked|Restricted)/i;

      const parts = children.split(regex);

      return (
        <>
          {parts.map((part, i) => {
            if (!part) return;

            const isTarget =
              part === 'Locked' || part === 'Unlocked' || part === 'Restricted';

            return isTarget ? (
              <strong key={i}>{part}</strong>
            ) : (
              <React.Fragment key={i}>{part}</React.Fragment>
            );
          })}
        </>
      );
    }

    return <>{children}</>;
  };

  return (
    <Card sx={{ padding: '0 0.5rem' }}>
      <CardHeader
        title='Primary Image'
        action={
          <Button onClick={showEditIdShotModal} variant='outlined'>
            Edit Primary Image
          </Button>
        }
      />

      <CardContent
        sx={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}
      >
        <Typography>
          Allowing T1 Image swap with pre-defined image shot types like T2, T596
          etc.
        </Typography>
        <Stack direction='row' spacing={1}>
          <Typography fontWeight={600}>
            Access:{' '}
            <span style={{ fontWeight: 'normal' }}>
              {idShot.access
                .charAt(0)
                .toLocaleUpperCase()
                .concat(idShot.access.slice(1))}
            </span>
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'left',
            }}
          >
            <Tooltip
              placement='top'
              title={
                <span>
                  {CONNECT_MAX_T1_ACCESS_DESCRIPTION.map((node, index) => (
                    <List key={`locked-title-${index}`}>
                      <BoldIfIncludesStatus>{node}</BoldIfIncludesStatus>
                    </List>
                  ))}
                </span>
              }
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Icon
                  iconName='info'
                  fontSize='small'
                  sx={{
                    color: 'grey.800',
                    zIndex: (theme) => theme.zIndex.modal - 1,
                  }}
                />
              </div>
            </Tooltip>
          </Box>
        </Stack>

        <Typography>
          {idShot.access === 'restricted' && (
            <Stack
              direction='row'
              spacing={1}
              alignItems='center'
              flexWrap='wrap'
              sx={{ rowGap: 1 }}
            >
              <Typography fontWeight={600}>Permitted Shot Types:</Typography>
              {[...idShot.permittedShotTypes]
                .sort((a, b) => a - b)
                .map((type) => (
                  <Chip
                    key={type}
                    label={
                      <Typography fontWeight={600} fontSize='1rem'>
                        {type}
                      </Typography>
                    }
                    variant='outlined'
                    color='primary'
                    sx={{
                      p: '0.4125rem',
                      height: '2.25rem',
                    }}
                  />
                ))}
            </Stack>
          )}
        </Typography>
      </CardContent>
    </Card>
  );
};

export default IdShotWidget;
